import React, { useState } from "react";
import apiEndpoints from "@store/apiEndpoints";
import useApiResource from "@pages/TicketNew/useApiResource";
import { Card, CardTitle } from "../ui/Card";
import TextField from "../ui/TextField";
import SearchableSelectField from "../ui/SearchableSelectField";
import { isFutureIsoDate, todayIsoDate } from "../validation";
import { CLAIM_TYPES } from "../options";

export default function ProductSection({
  form,
  protectedFetch,
  claimIsOther,
  porRequired,
  attempted = false,
  isCustomer = false,
}) {
  const { values, setField, setValues } = form;
  const [popDateTouched, setPopDateTouched] = useState(false);
  const [porDateTouched, setPorDateTouched] = useState(false);
  const today = todayIsoDate();

  const claimTypeOptions = CLAIM_TYPES.map((claimType) => ({ value: claimType, label: claimType }));

  const brands = useApiResource(
    protectedFetch,
    apiEndpoints.portal.brands(),
    null,
  );
  const brandOptions = (brands.data ?? []).map((brand) => ({
    value: String(brand.id),
    label: brand.name,
  }));

  const selectedBrandId = values.brandId ? Number(values.brandId) : undefined;
  const devices = useApiResource(
    protectedFetch,
    apiEndpoints.portal.devices(selectedBrandId),
    null,
    { skip: !selectedBrandId },
  );
  const deviceOptions = (devices.data ?? []).map((device) => ({
    value: String(device.id),
    label: device.name,
  }));

  const showPopDateError = popDateTouched || attempted;
  const popDateError = !showPopDateError
    ? undefined
    : isFutureIsoDate(values.popDate)
      ? "Purchase date cannot be in the future"
      : undefined;

  const showPorDateError = porRequired && (porDateTouched || attempted);
  const porDateError = !showPorDateError
    ? undefined
    : isFutureIsoDate(values.porDate)
      ? "Return date cannot be in the future"
      : values.popDate !== "" && values.porDate !== "" && values.porDate < values.popDate
        ? "Return date cannot be before the purchase date"
        : undefined;

  return (
    <Card radius="panel">
      <CardTitle>Product</CardTitle>

      <div className="rma-nc-grid-2">
        {!isCustomer && (
          <SearchableSelectField
            label="Reason for submission"
            required
            placeholder="Search for a reason…"
            value={values.claimType}
            options={claimTypeOptions}
            isClearable={false}
            onChange={(claimType) => setField("claimType", claimType)}
          />
        )}

        {claimIsOther && (
          <TextField
            label="Please specify"
            size="md"
            required
            placeholder="Describe your claim type"
            value={values.claimTypeOther}
            onChange={(event) => setField("claimTypeOther", event.target.value)}
          />
        )}

        <SearchableSelectField
          label="Brand"
          required
          placeholder="Search for a brand…"
          value={values.brandId}
          options={brandOptions}
          isLoading={brands.loading}
          onChange={(value) => setValues({ ...values, brandId: value, deviceId: "" })}
        />
        <SearchableSelectField
          label="Device"
          required
          placeholder="Search for a device…"
          value={values.deviceId}
          options={deviceOptions}
          isLoading={devices.loading}
          isDisabled={!selectedBrandId}
          onChange={(value) => setField("deviceId", value)}
        />
        <TextField
          label="Serial Number"
          size="md"
          placeholder="S/N"
          value={values.serialNumber}
          onChange={(event) => setField("serialNumber", event.target.value)}
        />
        <div>
          <TextField
            label="DOP (Date of Purchase)"
            size="md"
            type="date"
            max={today}
            value={values.popDate}
            onChange={(event) => setField("popDate", event.target.value)}
            onBlur={() => setPopDateTouched(true)}
            className={popDateError ? "rma-nc-control--error" : undefined}
          />
          {popDateError && <p className="rma-nc-error-text">{popDateError}</p>}
        </div>
        {!isCustomer && (
          <div>
            <TextField
              label="DOR (Date of Return)"
              size="md"
              type="date"
              min={values.popDate || undefined}
              max={today}
              disabled={!porRequired}
              hint={!porRequired ? "Not required for warranty service claims" : undefined}
              value={porRequired ? values.porDate : ""}
              onChange={(event) => setField("porDate", event.target.value)}
              onBlur={() => setPorDateTouched(true)}
              className={porDateError ? "rma-nc-control--error" : undefined}
            />
            {porDateError && <p className="rma-nc-error-text">{porDateError}</p>}
          </div>
        )}
        {!isCustomer && (
          <TextField
            label="Solvup Case"
            size="md"
            note="optional"
            info="Enter your Solvup case number here. This can be used when following up on an existing claim with our support team, making it easier to locate and track your request."
            placeholder="Case reference"
            value={values.solvupCase}
            onChange={(event) => setField("solvupCase", event.target.value)}
          />
        )}
        {!isCustomer && (
          <TextField
            label="Store Reference"
            size="md"
            note="optional"
            info="Enter your store reference number here. This can be used when following up on an existing claim with our support team, making it easier to locate and track your request."
            placeholder="Store reference"
            value={values.storeReference}
            onChange={(event) => setField("storeReference", event.target.value)}
          />
        )}
      </div>
    </Card>
  );
}
